import { useState, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Slider,
  Select,
  MenuItem,
  TextField,
  Button,
  FormControl,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors } from "./services.styles";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const ServiceFilterDropdown = ({ onApply, onClose, initialFilters = {}, allServices = [] }) => {
  const theme = useTheme();
  const colors = getColors(theme);

  // Use dynamic services from props, fallback to defaults
  const categories = useMemo(
    () => (allServices.length > 0 ? allServices.map((s) => s.charAt(0).toUpperCase() + s.slice(1)) : ["Skin", "Dental", "Hair"]),
    [allServices]
  );

  const [selectedCategories, setSelectedCategories] = useState(
    initialFilters.categories || []
  );
  const [conversionRate, setConversionRate] = useState(
    initialFilters.conversionRate || ""
  );
  const [revenueRange, setRevenueRange] = useState(
    initialFilters.revenueRange || { min: 0, max: "" }
  );

  const handleCategoryToggle = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const handleClearAll = () => {
    setSelectedCategories([]);
    setConversionRate("");
    setRevenueRange({ min: 0, max: "" });
  };

  const handleApply = () => {
    onApply?.({
      categories: selectedCategories,
      conversionRate,
      revenueRange,
    });
  };

  return (
    <Box sx={{ width: 320, maxHeight: "80vh", overflowY: "auto", p: 2.5 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: colors.textPrimary,
          }}
        >
          Filter Services
        </Typography>
        {allServices.length > 0 && (
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 500,
              color: colors.textMuted,
            }}
          >
            {allServices.length} services available
          </Typography>
        )}
      </Stack>

      {/* Service Category Chips */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: colors.textMuted,
            textTransform: "uppercase",
            mb: 1.5,
          }}
        >
          SERVICE CATEGORY
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {categories.slice(0, 8).map((category) => {
            const isSelected = selectedCategories.includes(category);
            return (
              <Chip
                key={category}
                label={category}
                onClick={() => handleCategoryToggle(category)}
                onDelete={isSelected ? () => handleCategoryToggle(category) : undefined}
                deleteIcon={isSelected ? <CloseIcon sx={{ fontSize: 14 }} /> : undefined}
                sx={{
                  height: 28,
                  fontSize: 12,
                  fontWeight: 500,
                  bgcolor: isSelected ? colors.primary : colors.bgPrimary,
                  color: isSelected ? "#fff" : colors.textSecondary,
                  border: `1px solid ${isSelected ? colors.primary : colors.border}`,
                  borderRadius: 2,
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                  "& .MuiChip-deleteIcon": {
                    color: isSelected ? "#fff" : colors.textMuted,
                    "&:hover": {
                      color: isSelected ? "#fff" : colors.textPrimary,
                    },
                  },
                  "&:hover": {
                    bgcolor: isSelected ? colors.primaryDark : colors.bgHover,
                  },
                }}
              />
            );
          })}
          {categories.length > 8 && (
            <Chip
              label={`+${categories.length - 8} more`}
              sx={{
                height: 28,
                fontSize: 11,
                fontWeight: 500,
                bgcolor: colors.bgPrimary,
                color: colors.textMuted,
                border: `1px dashed ${colors.border}`,
                borderRadius: 2,
                cursor: "default",
              }}
            />
          )}
        </Stack>
      </Box>

      {/* Conversion Rate */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: colors.textMuted,
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          MIN CONVERSION RATE %
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={conversionRate}
            onChange={(e) => setConversionRate(e.target.value)}
            displayEmpty
            IconComponent={ExpandMoreIcon}
            sx={{
              borderRadius: 2,
              fontSize: 13,
              bgcolor: colors.bgPrimary,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.border,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.primary,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.primary,
                borderWidth: 1,
              },
            }}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="5">&gt; 5%</MenuItem>
            <MenuItem value="10">&gt; 10%</MenuItem>
            <MenuItem value="15">&gt; 15%</MenuItem>
            <MenuItem value="20">&gt; 20%</MenuItem>
            <MenuItem value="25">&gt; 25%</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Revenue Range */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: colors.textMuted,
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          REVENUE RANGE (₹)
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            placeholder="Min"
            size="small"
            type="number"
            value={revenueRange.min || ""}
            onChange={(e) => setRevenueRange((prev) => ({ ...prev, min: e.target.value }))}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
                bgcolor: colors.bgPrimary,
                "& fieldset": {
                  borderColor: colors.border,
                },
                "&:hover fieldset": {
                  borderColor: colors.primary,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 1,
              },
            }}
          />
          <TextField
            placeholder="Max"
            size="small"
            type="number"
            value={revenueRange.max || ""}
            onChange={(e) => setRevenueRange((prev) => ({ ...prev, max: e.target.value }))}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
                bgcolor: colors.bgPrimary,
                "& fieldset": {
                  borderColor: colors.border,
                },
                "&:hover fieldset": {
                  borderColor: colors.primary,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 1,
              },
            }}
          />
        </Stack>
      </Box>

      {/* Actions */}
      <Stack direction="row" spacing={2}>
        <Button
          variant="text"
          onClick={handleClearAll}
          sx={{
            flex: 1,
            fontSize: 13,
            fontWeight: 600,
            color: colors.textSecondary,
            textTransform: "none",
            "&:hover": {
              bgcolor: colors.bgHover,
            },
          }}
        >
          Clear All
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            flex: 1,
            fontSize: 13,
            fontWeight: 600,
            bgcolor: colors.primary,
            color: "#fff",
            textTransform: "none",
            borderRadius: 2,
            boxShadow: "none",
            "&:hover": {
              bgcolor: colors.primaryDark,
              boxShadow: "none",
            },
          }}
        >
          Apply
        </Button>
      </Stack>
    </Box>
  );
};

export default ServiceFilterDropdown;
